var FakeCation = FakeCation || {};

// From:
// https://bitbucket.org/dyuri/colordetect.js

(function (ns) {
  "use strict";

  /**
   * Color class
   * @constructor
   * @param {number} r red component (0-255)
   * @param {number} g green component (0-255)
   * @param {number} b blue component (0-255)
   * @param {number=} a alpha (0-1)
   */
  var Color = function (r, g, b, a) {
    this.red = parseInt(r, 10);
    this.green = parseInt(g, 10);
    this.blue = parseInt(b, 10);
    this.alpha = a === 0 ? 0 : parseFloat(a || 1);
  };

  /**
   * check if the color is valid
   * @return {boolean}
   */
  Color.prototype.isValid = function () {
    return this.red >= 0 && this.red <= 255 &&
      this.green >= 0 && this.green <= 255 &&
      this.blue >= 0 && this.blue <= 255 &&
      this.alpha <= 1 && this.alpha >= 0;
  };

  /**
   * toString for colors
   * @return {string}
   */
  Color.prototype.toString = function () {
    var result;
    if (this.alpha !== 1) {
      result = "rgba(" + [this.red, this.green, this.blue, this.alpha].join(", ") + ")";
    } else {
      result = "rgb(" + [this.red, this.green, this.blue].join(", ") + ")";
    }
    return result;
  };

  /**
   * Some constant colors
   */
  Color.RED = new Color(255, 0, 0);
  Color.GREEN = new Color(0, 255, 0);
  Color.BLUE = new Color(0, 0, 255);
  Color.YELLOW = new Color(255, 255, 0);
  Color.MAGENTA = new Color(255, 0, 255);
  Color.CYAN = new Color(0, 255, 255);
  Color.WHITE = new Color(255, 255, 255);
  Color.BLACK = new Color(0, 0, 0);

  /**
   * return the complementer color
   * @return {Color}
   */
  Color.prototype.invert = function () {
    return new Color(255 - this.red, 255 - this.green, 255 - this.blue, this.alpha);
  };

  /**
   * Hex code of the color
   * @return {string} the hex code
   */
  Color.prototype.toHex = function () {
    var r = this.red >= 16 ? this.red.toString(16) : '0' + this.red.toString(16),
      g = this.green >= 16 ? this.green.toString(16) : '0' + this.green.toString(16),
      b = this.blue >= 16 ? this.blue.toString(16) : '0' + this.blue.toString(16);

    return '#' + r + g + b;
  };

  /**
   * HSL values of the color
   * @return {Array.<number>} [hue, saturation, lightning]
   */
  Color.prototype.toHsl = function () {
    // caching
    if (this.h && this.s && this.l) {
      return [this.h, this.s, this.l];
    }

    var r = this.red / 255,
      g = this.green / 255,
      b = this.blue / 255,
      max = Math.max(r, g, b),
      min = Math.min(r, g, b),
      d, h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h = h / 6;
    }

    this.h = h;
    this.s = s;
    this.l = l;
    return [h, s, l];
  };

  /**
   * Compares two color objects if they are similar depending on the given parameters
   * @param {Color} color the color to compare
   * @param {number=} hdiff max hue difference
   * @param {number=} sdiff max saturation difference
   * @param {number=} ldiff max lightness difference
   * @return {boolean} true if they are similar, false otherwise
   */
  Color.prototype.similarHsl = function (color, hdiff, sdiff, ldiff) {
    // hue - max 0.01 difference
    // saturation - max 0.2 difference
    var hsl1 = this.toHsl(),
        hsl2 = color.toHsl();

    hdiff = hdiff || 0.01;
    sdiff = sdiff || 0.2;
    ldiff = ldiff || 0.5;

    return Math.abs(hsl1[0] - hsl2[0]) < hdiff && Math.abs(hsl1[1] - hsl2[1]) < sdiff && Math.abs(hsl1[2] - hsl2[2]) < ldiff;
  };

  /**
   * Compares two color based on RGB values
   * not as good as similarHsl, but much quicker
   * @param {Color} color
   * @param {number=} threshold
   */
  Color.prototype.similarRgb = function (color, threshold) {
    threshold = threshold || 40;
    return this.diff(color) < threshold;
  };

  Color.prototype.similar = Color.prototype.similarHsl;

  /**
   * Compares two color based on RGB values if they are the same or not
   * @param {Color} color
   */
  Color.prototype.same = function (color) {
    return this.diff(color) === 0;
  };

  /**
   * counts the similar pixels on a canvas
   * returns if optional maximum value reached
   * @param {HTMLCanvasElement} canvas
   * @param {number=} max
   * @return {number}
   */
  Color.prototype.countSimilars = function (canvas, max) {
    var ctx = canvas.getContext("2d"),
      imgData = ctx.getImageData(0, 0, canvas.width, canvas.height),
      data = imgData.data,
      i, tmpc, num = 0;

    for (i = 0; i < data.length; i += 4) {
      tmpc = new Color(data[i], data[i + 1], data[i + 2]);
      if (tmpc.similar(this)) {
        num++;
        if (max && num >= max) {
          break;
        }
      }
    }

    return max ? num >= max : num;
  };

  /**
   * "Difference" between colors
   * @param {Color} color the color to compare
   * @return {number} the difference
   */
  Color.prototype.diffHsl = function (color) {
    var hsl1 = this.toHsl(),
      hsl2 = color.toHsl();

    return Math.abs(hsl1[0] - hsl2[0]) * 10 + Math.abs(hsl1[1] - hsl2[1]);
  };

  /**
   * "Difference" between colors, based on rgb values
   * @param {Color} color the color to compare
   * @return {number} the difference
   */
  Color.prototype.diffRgb = function (color) {
    var rd = this.red - color.red,
      gd = this.green - color.green,
      bd = this.blue - color.blue;

    return Math.sqrt(rd * rd + gd * gd + bd * bd);
  };

  Color.prototype.diff = Color.prototype.diffRgb;

  /**
   * Return a new color object with the "average color" of the given list
   * @param {Array.<Color>} clist list of colors
   * @return {Color} the average color
   */
  Color.average = function (clist) {
    var r = 0,
      g = 0,
      b = 0,
      a = 0,
      i, l = clist.length;

    for (i = 0; i < l; i++) {
      r += clist[i].red;
      g += clist[i].green;
      b += clist[i].blue;
      a += clist[i].alpha;
    }

    return new Color(r / l, g / l, b / l, a / l);
  };

  /**
   * Utilities for Color class
   */
  var colorUtils = {};

  /**
   * get color from the given position of an imgData object
   * @param {ImageData} imgData
   * @param {number} x
   * @param {number} y
   * @return {Color}
   */
  colorUtils.getColor = function (imgData, x, y) {
    var offset = (y * imgData.width + x) * 4,
      d = imgData.data;

    return new Color(d[offset], d[offset + 1], d[offset + 2], d[offset + 3] / 255);
  };

  /**
   * set color of the given position of an imgData object
   * @param {ImageData} imgData
   * @param {number} x
   * @param {number} y
   * @param {Color} color
   */
  colorUtils.setColor = function (imgData, x, y, color) {
    var offset = (y * imgData.width + x) * 4;
    imgData.data[offset] = color.red;
    imgData.data[offset + 1] = color.green;
    imgData.data[offset + 2] = color.blue;
    imgData.data[offset + 3] = +(color.alpha * 255) | 0;
  };

  colorUtils.getColors = function (imgData, points) {
    return points.map(point => colorUtils.getColor(imgData, parseInt(point[0], 10), parseInt(point[1], 10)));
  };

  /**
   * flood fill the selected area with the given color
   * @param {ImageData} imgData
   * @param {number} startX
   * @param {number} startY
   * @param {Color} fillColor
   * @param {function} matcherFn (currentColor, startColor, fillColor, lastColor, imgData, x, y)
   * @param {function} colorFn (currentColor, startColor, fillColor, lastColor, imgData, x, y)
   */
  colorUtils.floodFill = function (imgData, startX, startY, fillColor, matcherFn, colorFn) {
    var newPos,
      x, y,
      currentColor,
      startColor = colorUtils.getColor(imgData, startX, startY),
      lastColor,
      reachLeft,
      reachRight,
      drawingBoundLeft = 0,
      drawingBoundTop = 0,
      drawingBoundRight = imgData.width - 1,
      drawingBoundBottom = imgData.height - 1,
      pixelStack = [
        {x: startX, y: startY}
      ];

    matcherFn = matcherFn || function (cColor, sColor, fColor) {
      return !fColor.same(cColor) && sColor.similarRgb(cColor);
    };

    colorFn = colorFn || function (cColor, sColor, fColor, lColor, iData, cx, cy) {
      colorUtils.setColor(iData, cx, cy, fColor);
    };

    while (pixelStack.length) {
      newPos = pixelStack.pop();
      x = newPos.x;
      y = newPos.y;
      lastColor = newPos.lastColor;

      currentColor = colorUtils.getColor(imgData, x, y);

      while (y >= drawingBoundTop && matcherFn(currentColor, startColor, fillColor, lastColor, imgData, x, y)) {
        y -= 1;
        lastColor = currentColor;
        currentColor = colorUtils.getColor(imgData, x, y);
      }

      y += 1;
      reachLeft = false;
      reachRight = false;
      currentColor = colorUtils.getColor(imgData, x, y);

      while (y <= drawingBoundBottom && matcherFn(currentColor, startColor, fillColor, lastColor, imgData, x, y)) {

        colorFn(currentColor, startColor, fillColor, lastColor, imgData, x, y);

        if (x > drawingBoundLeft) {
          if (matcherFn(colorUtils.getColor(imgData, x - 1, y), startColor, fillColor, lastColor, imgData, x - 1, y)) {
            if (!reachLeft) {
              pixelStack.push({x: x - 1, y: y, lastColor: lastColor});
              reachLeft = true;
            }
          } else if (reachLeft) {
            reachLeft = false;
          }
        }

        if (x < drawingBoundRight) {
          if (matcherFn(colorUtils.getColor(imgData, x + 1, y), startColor, fillColor, lastColor, imgData, x + 1, y)) {
            if (!reachRight) {
              pixelStack.push({x: x + 1, y: y, lastColor: lastColor});
              reachRight = true;
            }
          } else if (reachRight) {
            reachRight = false;
          }
        }

        y += 1;
        lastColor = currentColor;
        currentColor = colorUtils.getColor(imgData, x, y);
      }
    }

    return imgData;
  };

  /**
   * r/g/b histogram
   * @param {ImageData} imgData
   */
  colorUtils.histogram = function (imgData) {
    var a256 = function (dValue) {
          var i, arr = [];
          for (i = 0; i < 256; i++) {
            arr[i] = dValue;
          }
          return arr;
        },
        normalize = function (arr) {
          var max = Math.max.apply(null, arr);

          return arr.map(function (v) {
            return 100 * v / max;
          });
        },
        rHist = a256(0),
        gHist = a256(0),
        bHist = a256(0),
        bwHist = a256(0),
        x, y, c;

    for (y = 0; y < imgData.height; y++) {
      for (x = 0; x < imgData.width; x++) {
        c = colorUtils.getColor(imgData, x, y);
        rHist[c.red]++;
        gHist[c.green]++;
        bHist[c.blue]++;
        bwHist[parseInt((c.red+c.green+c.blue)/3, 10)]++;
      }
    }

    return {
      red: normalize(rHist),
      green: normalize(gHist),
      blue: normalize(bHist),
      grayscale: normalize(bwHist),
      rawred: rHist,
      rawgreen: gHist,
      rawblue: bHist,
      rawgrayscale: bwHist
    };
  };

  /**
   * draw histogram on a canvas
   * @param {Array.<number>} histogram
   * @param {Color} color drawing color
   * @param {HTMLCanvas} canvas canvas to draw on
   * @param {number=} x x of top left coordinate
   * @param {number=} y y of top left coordinate
   * @param {number=} w width of the histogram
   * @param {number=} h height of the histogram
   */
  colorUtils.drawHistogram = function (histogram, color, canvas, x, y, w, h) {
    var ctx = canvas.getContext('2d'), w1, h1;

    x = x || 0;
    y = y || 0;
    h = h || 100;
    w = w || 256;

    w1 = w/256;
    h1 = h/100;

    ctx.fillStyle = color.toString();
    histogram.forEach(function (v, i) {
      ctx.fillRect(x+w1*i|0, y+h|0, w1|0, -h1*v|0);
    });
  };

  /**
   * get histogram borders
   * @param {ImageData} imgData
   * @return {ImageData}
   */
  colorUtils.histogramBorders = function (imgData, threshold, border) {
    var histData = colorUtils.histogram(imgData),
        borders = {},
        numPixels = imgData.width * imgData.height,
        findMinMax = function (arr, thr, bdr) {
          var min = bdr, max = 255 - bdr;

          while (min < 128 && arr[min] < thr * numPixels) { min++; }
          while (max > 128 && arr[max] < thr * numPixels) { max--; }

          return {min: min, max: max};
        };

    threshold = threshold === 0 ? 0 : threshold || 0.001;
    border = border === 0 ? 0 : border || 2;

    // find min / max values above threshold
    ['red', 'green', 'blue'].forEach(function (component) {
      borders[component] = findMinMax(histData['raw'+component], threshold, border);
    });

    return borders;
  };

  /**
   * transform histogram
   * @param {ImageData} imgData
   * @param {Object} from histogram range to transform from
   * @param {Object} to histogram range to transform to
   * @return {ImageData}
   */
  colorUtils.transformHistogram = function (imgData, from, to) {
    var x, y, color,
        transformColor = function (c, f, t) {
          ['red', 'green', 'blue'].forEach(function (component) {
            var cFrom = f[component],
                cTo = t[component];

            c[component] = Math.max(Math.min(cTo.min + cTo.max * (c[component] - cFrom.min) / (cFrom.max - cFrom.min), 255), 0);
          });

          return c;
        };

    to = to || {
      red: {min: 0, max: 255},
      green: {min: 0, max: 255},
      blue: {min: 0, max: 255}
    };

    for (y = 0; y < imgData.height; y++) {
      for (x = 0; x < imgData.width; x++) {
        color = colorUtils.getColor(imgData, x, y);
        colorUtils.setColor(imgData, x, y, transformColor(color, from, to));
      }
    }

    return imgData;
  };

  /**
   * strech rgb contrast
   * @param {ImageData} imgData
   * @param {number=} threshold histogram minimum threshold to ignore
   * @param {Object} border low/high borders of histogram mapping
   * @return {ImageData}
   */
  colorUtils.strechContrast = function (imgData, threshold, border) {
    var borders = colorUtils.histogramBorders(imgData, threshold, border);

    return colorUtils.transformHistogram(imgData, borders);
  };

  /**
   * get the average color from the given area of an imgData object
   * @param {ImageData} imgData
   * @param {number} x
   * @param {number} y
   * @param {number=} radius radius of the area
   * @return {Color}
   */
  colorUtils.getRefColor = function (imgData, x, y, radius) {
    var i, j, cl = [];

    radius = radius || 3;

    for (i = x - radius; x + radius >= i; i++) {
      for (j = y - radius; y + radius >= j; j++) {
        cl.push(colorUtils.getColor(imgData, i, j));
      }
    }

    return Color.average(cl);
  };

  colorUtils.getPixels = function (context, color) {
    var idata, width, height,
      x, y, offset,
      result = [];

    if (context.getImageData) {
      idata = context.getImageData(0, 0, context.canvas.width, context.canvas.height);
    } else if (context.data && context.height && context.width) {
      idata = context;
    }

    width = idata.width;
    height = idata.height;

    for (y = 0; y < height; y++) {
      for (x = 0; x < width; x++) {
        offset = (y * width + x) * 4;
        if (idata.data[offset] === color.red &&
          idata.data[offset + 1] === color.green &&
          idata.data[offset + 2] === color.blue &&
          idata.data[offset + 3] === (+(color.alpha * 255) | 0)) {
          result.push([x, y]);
        }
      }
    }

    return result;
  };

  colorUtils.Color = Color;
  ns.colorUtils = colorUtils;

}(FakeCation));
